import { useApi } from '@core/api'
import { InternalCreateCustomerInput, InternalUpdateCustomerInput } from '@core/api/generated'
import { useMainState } from '@core/main-state'
import { addSnack } from '@core/snack/snack-state'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

export const keys = {
  getCustomer: ['admin', 'getCustomer'],
  listCustomers: ['admin', 'listCustomers'],
  updateCustomer: ['admin', 'updateCustomer'],
  createCustomer: ['admin', 'createCustomer'],
  switchCustomer: ['admin', 'switchCustomer'],
}

export const useCustomer = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: keys.getCustomer,
    queryFn: async () => {
      const { data } = await api.internalGetCustomer({ throwOnError: true })

      return data
    },
  })
}

export const useListCustomers = () => {
  const api = useApi()

  return useSuspenseQuery({
    queryKey: keys.listCustomers,
    queryFn: async () => {
      const { data } = await api.internalListCustomers({ throwOnError: true })
      return data
    },
  })
}

export const useSwitchCustomer = () => {
  const api = useApi()

  return useMutation({
    mutationKey: keys.switchCustomer,
    mutationFn: async (customerId: string): Promise<{ status?: string; message?: string }> => {
      await api.switchCustomer({ path: { customer_id: customerId } })
      return { status: 'ok' }
    },
    onSuccess: () => {
      addSnack({ message: 'Customer switched, refreshing...', severity: 'info' })
    },
    onError: () => {
      const message = 'Failed to switch customer'
      addSnack({ message, severity: 'error' })
      return { status: 'error', message }
    },
  })
}

export const useUpdateCustomer = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: keys.updateCustomer,
    mutationFn: async (body: InternalUpdateCustomerInput) => {
      const { data } = await api.internalUpdateCustomer({
        body,
        throwOnError: true,
      })

      await queryClient.invalidateQueries({
        queryKey: keys.getCustomer,
      })

      return data
    },
    onSuccess: (_data, variables) => {
      useMainState.setState((prev) => {
        const customer = { ...prev.customer! }
        if (variables.features != null)
          customer.features = variables.features as typeof customer.features
        if (variables.hasOptionRationale != null)
          customer.hasOptionRationale = variables.hasOptionRationale
        return { customer }
      })
      addSnack({ message: 'Customer updated', severity: 'success' })
    },
    onError: (error: Error) => {
      addSnack({ message: error.message, severity: 'error' })
    },
  })
}

export const useCreateCustomer = () => {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: keys.createCustomer,
    mutationFn: (body: InternalCreateCustomerInput) => {
      return api.internalCreateCustomer({ body })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.listCustomers })
    },
  })
}
